import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, CardBody, CardHeader, Table, Badge,
  Button, Spinner, Alert, Progress
} from 'reactstrap';
import { Clock, Activity, CheckCircle, AlertCircle, RefreshCw, Play } from 'react-feather';
import API from '../utils/api';

const Schedulers = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [health, setHealth] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [expandedLogs, setExpandedLogs] = useState({});

  useEffect(() => {
    loadSchedulerData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadSchedulerData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadSchedulerData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load health and jobs in parallel
      const [healthResponse, jobsResponse] = await Promise.all([
        API.get('/scheduler/health'),
        API.get('/scheduler/jobs')
      ]);

      setHealth(healthResponse.data);
      setJobs(jobsResponse.data.jobs);
      setLastUpdate(new Date());
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load scheduler data');
      console.error('Scheduler data error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryBadge = (category) => {
    const styles = {
      'epp_registry': 'bg-dark text-primary border border-primary',
      'domain_catching': 'bg-dark text-success border border-success',
      'sources': 'bg-dark text-info border border-info',
      'released_domains': 'bg-dark text-warning border border-warning',
      'system_maintenance': 'bg-dark text-secondary border border-secondary'
    };

    const labels = {
      'epp_registry': 'EPP Registry',
      'domain_catching': 'Domain Catch',
      'sources': 'Sources',
      'released_domains': 'Released',
      'system_maintenance': 'System'
    };

    return (
      <Badge className={styles[category] || 'bg-dark text-secondary border border-secondary'}>
        {labels[category] || category}
      </Badge>
    );
  };

  const getHealthBadge = (job) => {
    if (job.consecutive_failures >= 3) {
      return <Badge className="bg-dark text-danger border border-danger"><AlertCircle size={14} /> Failing</Badge>;
    }
    if (job.consecutive_failures > 0) {
      return <Badge className="bg-dark text-warning border border-warning"><AlertCircle size={14} /> Warning</Badge>;
    }
    if (job.total_executions > 0) {
      return <Badge className="bg-dark text-success border border-success"><CheckCircle size={14} /> Healthy</Badge>;
    }
    return <Badge className="bg-dark text-secondary border border-secondary">Pending</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleString();
  };

  const formatNextRun = (nextRunIso) => {
    if (!nextRunIso) return { time: '-', countdown: null };

    const nextRun = new Date(nextRunIso);
    const now = new Date();
    const diff = nextRun - now;

    let countdown = '';
    if (diff < 0) {
      countdown = 'Overdue';
    } else if (diff < 60000) {
      countdown = `in ${Math.floor(diff / 1000)}s`;
    } else if (diff < 3600000) {
      countdown = `in ${Math.floor(diff / 60000)}m`;
    } else if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      countdown = `in ${hours}h ${mins}m`;
    } else {
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      countdown = `in ${days}d ${hours}h`;
    }

    // Format the actual time
    const timeStr = nextRun.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    return { time: timeStr, countdown };
  };

  const toggleLogExpanded = (jobId) => {
    setExpandedLogs(prev => ({
      ...prev,
      [jobId]: !prev[jobId]
    }));
  };

  const handleReloadAll = async () => {
    try {
      setLoading(true);
      await API.post('/scheduler/reload');
      await loadSchedulerData();
      // Success notification can be added here
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reload schedulers');
    } finally {
      setLoading(false);
    }
  };

  const handleReloadCategory = async (category) => {
    try {
      await API.post(`/scheduler/reload/${category}`);
      await loadSchedulerData();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to reload category ${category}`);
    }
  };

  const handleRunNow = async (jobId) => {
    try {
      await API.post(`/scheduler/jobs/${jobId}/run`);
      // Reload data after a short delay to show the execution
      setTimeout(() => loadSchedulerData(), 1000);
    } catch (err) {
      setError(err.response?.data?.error || `Failed to run job ${jobId}`);
    }
  };

  if (loading && !health) {
    return (
      <Container fluid className="py-4">
        <div className="text-center py-5">
          <Spinner color="primary" />
          <p className="text-muted mt-2">Loading scheduler data...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="page-heading">
                <Clock className="page-heading-icon" />
                Scheduler Management
              </h2>
              <small className="text-muted">
                Centralized job scheduling and monitoring (UTC timezone)
              </small>
            </div>
            <div>
              <Button
                color="warning"
                onClick={handleReloadAll}
                disabled={loading}
                className="me-2"
              >
                <RefreshCw size={16} />
                {' '}Reload All Schedulers
              </Button>
              <Button
                color="primary"
                onClick={loadSchedulerData}
                disabled={loading}
              >
                <RefreshCw size={16} className={loading ? 'spinner' : ''} />
                {' '}Refresh
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <Alert color="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {lastUpdate && (
        <small className="text-muted d-block mb-3">
          Last updated: {lastUpdate.toLocaleTimeString()}
        </small>
      )}

      {/* Health Overview */}
      {health && (
        <Row className="mb-4">
          <Col md={12}>
            <Card className="bg-dark border-secondary">
              <CardHeader>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <Activity size={18} className="me-2" />
                    Scheduler Status & Categories
                  </div>
                  <div>
                    <Badge className={health.scheduler_running ? "bg-dark text-success border border-success" : "bg-dark text-danger border border-danger"}>
                      {health.scheduler_running ? 'Running' : 'Stopped'}
                    </Badge>
                    <span className="ms-3 text-muted">
                      Total: {health.total_jobs} jobs
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                <Table dark className="mb-0">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Total Jobs</th>
                      <th>Healthy</th>
                      <th>Failing</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(health.categories).map(([category, stats]) => (
                      <tr key={category}>
                        <td>{getCategoryBadge(category)}</td>
                        <td>{stats.total}</td>
                        <td>
                          <Badge className="bg-dark text-success border border-success">
                            {stats.healthy}
                          </Badge>
                        </td>
                        <td>
                          {stats.failing > 0 ? (
                            <Badge className="bg-dark text-danger border border-danger">
                              {stats.failing}
                            </Badge>
                          ) : (
                            <span className="text-muted">0</span>
                          )}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            color="outline-warning"
                            onClick={() => handleReloadCategory(category)}
                            title={`Reload ${category} schedulers`}
                          >
                            <RefreshCw size={14} className="me-1" />
                            Reload
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Active Jobs Table */}
      <Card className="bg-dark border-secondary">
        <CardHeader>
          <Clock size={18} className="me-2" />
          Active Jobs ({jobs.length})
        </CardHeader>
        <CardBody>
          <div className="table-responsive">
            <Table dark hover striped size="sm">
              <thead>
                <tr>
                  <th style={{width: '25%'}}>Job ID</th>
                  <th style={{width: '12%'}}>Category</th>
                  <th style={{width: '10%'}}>Next Run</th>
                  <th style={{width: '10%'}}>Last Run</th>
                  <th style={{width: '8%'}}>Runs</th>
                  <th style={{width: '12%'}}>Success</th>
                  <th style={{width: '10%'}}>Status</th>
                  <th style={{width: '13%'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center text-muted py-3">
                      No jobs scheduled
                    </td>
                  </tr>
                ) : (
                  jobs.map((job) => {
                    const healthJob = health?.jobs?.find(h => h.job_id === job.id);

                    return (
                      <React.Fragment key={job.id}>
                      <tr>
                        <td>
                          <div>
                            <small className="text-white">{job.name || job.id}</small>
                            {job.name && job.name !== job.id && (
                              <div className="text-muted" style={{fontSize: '10px'}}>
                                ID: {job.id}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          {healthJob && getCategoryBadge(healthJob.category)}
                        </td>
                        <td>
                          {(() => {
                            const nextRun = formatNextRun(job.next_run);
                            return (
                              <small>
                                <span className="text-muted">{nextRun.time}</span>
                                {nextRun.countdown && (
                                  <span className="text-info" style={{paddingLeft: '10px'}}>({nextRun.countdown})</span>
                                )}
                              </small>
                            );
                          })()}
                        </td>
                        <td>
                          <small className="text-muted">
                            {healthJob ? formatDate(healthJob.last_run) : 'Never'}
                          </small>
                        </td>
                        <td>
                          <small className="text-white">
                            {healthJob ? healthJob.total_executions : '-'}
                          </small>
                        </td>
                        <td>
                          {healthJob && healthJob.total_executions > 0 ? (
                            <div className="d-flex align-items-center">
                              <Progress
                                value={healthJob.success_rate}
                                color={healthJob.success_rate > 90 ? 'success' : healthJob.success_rate > 70 ? 'warning' : 'danger'}
                                style={{ height: '6px', width: '60px' }}
                                className="me-2"
                              />
                              <small className="text-muted">{healthJob.success_rate}%</small>
                            </div>
                          ) : (
                            <small className="text-muted">-</small>
                          )}
                        </td>
                        <td>
                          {healthJob ? getHealthBadge(healthJob) : <Badge className="bg-dark text-secondary border border-secondary">New</Badge>}
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              size="sm"
                              color="outline-success"
                              onClick={() => handleRunNow(job.id)}
                              title="Run job now"
                            >
                              <Play size={14} />
                            </Button>
                            {healthJob && healthJob.last_log && healthJob.last_log.length > 0 && (
                              <Button
                                size="sm"
                                color="outline-info"
                                onClick={() => toggleLogExpanded(job.id)}
                                title="View last execution log"
                              >
                                {expandedLogs[job.id] ? 'Hide' : 'Log'}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {/* Expandable log row */}
                      {expandedLogs[job.id] && healthJob && healthJob.last_log && healthJob.last_log.length > 0 && (
                        <tr className="bg-dark">
                          <td colSpan="8" className="p-0">
                            <div className="p-3 border-top border-secondary">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <strong className="text-white">
                                  <Activity size={14} className="me-1" />
                                  Last Execution Log
                                </strong>
                                <div>
                                  {healthJob.last_execution_duration && (
                                    <Badge className="bg-dark text-info border border-info me-2">
                                      Duration: {healthJob.last_execution_duration.toFixed(2)}s
                                    </Badge>
                                  )}
                                  <Badge className="bg-dark text-muted border border-secondary">
                                    {healthJob.last_log.length} lines
                                  </Badge>
                                </div>
                              </div>
                              <pre className="bg-black text-light p-3 rounded border border-secondary" style={{ fontSize: '11px', maxHeight: '300px', overflow: 'auto', marginBottom: 0 }}>
                                {healthJob.last_log.join('\n')}
                              </pre>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>

      {/* Job Details */}
      {health?.jobs && health.jobs.length > 0 && (
        <Card className="bg-dark border-secondary mt-4">
          <CardHeader>
            <Activity size={18} className="me-2" />
            Job Health Details
          </CardHeader>
          <CardBody>
            {health.jobs
              .filter(job => job.consecutive_failures > 0 || job.last_error)
              .map(job => (
                <Alert key={job.job_id} color="warning" className="mb-2">
                  <strong>{job.job_id}</strong>
                  {job.consecutive_failures > 0 && (
                    <div className="mt-1">
                      <small>Consecutive failures: {job.consecutive_failures}</small>
                    </div>
                  )}
                  {job.last_error && (
                    <div className="mt-1">
                      <small className="text-danger">Last error: {job.last_error}</small>
                    </div>
                  )}
                </Alert>
              ))}

            {health.jobs.filter(job => job.consecutive_failures > 0 || job.last_error).length === 0 && (
              <div className="text-center text-success">
                <CheckCircle size={32} className="mb-2" />
                <div>All jobs are healthy!</div>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      <style>{`
        .spinner {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Container>
  );
};

export default Schedulers;
